import { useMutation, useQuery, useQueryClient } from 'react-query';
import dayjs from 'dayjs';

import type { IStandardErrorResponse } from 'src/api/common';
import { useNotifications } from 'src/notifications';
import { useCreateActivity } from 'src/services/api/activities';
import { apiClient } from 'src/services/api/client';
import { queryKeys } from 'src/services/api/constants';
import { playbooksPath } from 'src/services/api/playbooks/path';
import type { IPlaybookRun } from 'src/services/api/playbooks/types';
import { usePlaybooksV2 } from 'src/services/api/playbooks/use-playbooks';

export const ELIGIBLE_ROWS_QUERY_KEY = 'eligible-rows';

/**
 * Playbooks created before the numeric-id backfill have no id, so they cannot be addressed
 * by the API. Callers must disable id-requiring actions for them.
 */
const requireId = (id?: number | null): number => {
    if (id == null) {
        throw new Error('This playbook has no id yet');
    }
    return id;
};

export const useDeletePlaybook = (id?: number | null) => {
    const queryClient = useQueryClient();
    const { addNotification } = useNotifications();

    return useMutation<void, IStandardErrorResponse, void>(
        async () => {
            await apiClient(`${playbooksPath}/${requireId(id)}`, { method: 'delete' });
        }, {
            onSuccess: () => {
                queryClient.invalidateQueries(queryKeys.playbooksV2);
                addNotification('Playbook deleted', 'success');
            }
        },
    );
};

interface IPlaybookRunResponse {
    status: string;
    msg: string;
    run: IPlaybookRun;
}

interface IRunPlaybookPayload {
    playbookId: number | null;
    leadUUIDs: string[];
}

export const useRunPlaybook = () => {
    const queryClient = useQueryClient();
    const { addNotification } = useNotifications();
    const { mutateAsync: createActivity } = useCreateActivity();

    const { data: playbooks } = usePlaybooksV2();

    return useMutation<IPlaybookRunResponse, unknown, IRunPlaybookPayload>(
        async (payload) => {
            return apiClient(`${playbooksPath}/${requireId(payload.playbookId)}/trigger`, {
                method: 'post',
                json: { lead_uuids: payload.leadUUIDs },
                // playbook runs are slow to queue, keep the legacy 25s timeout
                timeout: 25000,
            }).json<IPlaybookRunResponse>();
        }, {
            onMutate: () => {
                addNotification('Playbook run confirmed. In progress...', 'info');
            },
            onSuccess: async (data) => {
                const success = data.status === 'success';
                const message = success ? data.msg : 'Run failed';
                const variant = success ? 'success' : 'error';
                addNotification(message, variant);
                if (success) {
                    createActivity({ time: dayjs().format(), type: 'manual_playbook_run' }).catch(() => {
                        console.error('Failed to create activity');
                    });
                }
                queryClient.invalidateQueries(queryKeys.playbooksV2);
                playbooks?.forEach((playbook) => {
                    queryClient.invalidateQueries(`${ELIGIBLE_ROWS_QUERY_KEY}-${playbook.id}`);
                });
            },
            onError: () => {
                addNotification('Run failed', 'error');
            },
        }
    );
};

export interface IEligibleRowsResponse {
    num_eligible_rows: number;
    eligible_lead_uuids: string[];
}

export const useEligibleRows = (id?: number | null) => {
    return useQuery<IEligibleRowsResponse>(
        `${ELIGIBLE_ROWS_QUERY_KEY}-${id}`,
        async () => apiClient(`${playbooksPath}/${id}/num-eligible-rows`).json<IEligibleRowsResponse>(),
        {
            enabled: id != null,
        }
    );
};

export * from './use-create-playbook';
export * from './use-playbooks';
export * from './use-playbook-variables';
export * from './use-update-playbook';
export * from './use-update-background-playbooks-order';
export * from './utils';

export * from './types';
